import {
  app_version,
  flushAllPendingAppsForLogout,
  hasAnyPendingUpload,
} from "@workspace/shared";
import modernLogo from "../../../assets/mofoIcon.png";
import { useAuth } from "@workspace/ui/hooks";
import {
  LogoutSyncOverlay,
  LogoutOfflineWarning,
  LogoutSyncFailedWarning,
} from "@workspace/ui";
import { FC, MouseEvent, ReactNode, useState } from "react";

interface MainLayoutProps {
  showHeader: boolean;
  children: ReactNode;
}

const MainLayout: FC<MainLayoutProps> = ({ children, showHeader }) => {
  const { logout, isAuthenticated } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [offlineWarn, setOfflineWarn] = useState(false);
  const [syncFailWarn, setSyncFailWarn] = useState(false);

  const doLogout = async (force = false) => {
    setSigningOut(true);
    // Upload every app's pending plays before wiping this device on sign-out.
    let flushedOk = true;
    if (navigator.onLine) {
      try {
        flushedOk = await flushAllPendingAppsForLogout();
      } catch (error) {
        flushedOk = false;
        console.warn("Unable to flush pending plays before logout", error);
      }
    }
    // logout() wipes every app's local data — if the upload failed (expired
    // session, server error) AND unsynced data is still on this device, block
    // the sign-out instead of destroying it. `force` comes from the explicit
    // "Log out anyway" button in the warning dialog.
    if (!force && !flushedOk && hasAnyPendingUpload()) {
      setSigningOut(false);
      setSyncFailWarn(true);
      return;
    }
    await logout();
  };

  const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Offline with unsaved plays: they can't be uploaded, so warn first.
    if (typeof navigator !== "undefined" && !navigator.onLine && hasAnyPendingUpload()) {
      setOfflineWarn(true);
      return;
    }
    void doLogout();
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <LogoutSyncOverlay open={signingOut} />
      <LogoutOfflineWarning
        open={offlineWarn}
        onCancel={() => setOfflineWarn(false)}
        onConfirm={() => {
          setOfflineWarn(false);
          void doLogout(true);
        }}
      />
      <LogoutSyncFailedWarning
        open={syncFailWarn}
        onCancel={() => setSyncFailWarn(false)}
        onConfirm={() => {
          setSyncFailWarn(false);
          void doLogout(true);
        }}
      />
      {showHeader && (
        <header className="flex w-full items-center justify-between bg-transparent px-8 pt-8">
          <div className="w-full tracking-wide">
            <img
              id="modernLogo"
              src={modernLogo}
              alt="modern football"
              width={"130px"}
              height={"40px"}
            />
          </div>
          {isAuthenticated && (
            <div className="relative pr-3">
              <button
                className="w-full cursor-pointer rounded-md bg-blue-600 p-2 px-3.5 font-semibold text-white transition hover:bg-blue-700"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )}
        </header>
      )}

      <main className="flex-1 overflow-y-auto px-4 pb-10">{children}</main>

      <footer className="p-4">
        <p className="text-center text-[14px] text-xs leading-[22px] tracking-normal text-gray-500 opacity-70">
          © {new Date().getFullYear()} Modern Football, Inc. All rights reserved, {app_version}
        </p>
      </footer>
    </div>
  );
};

export default MainLayout;
